// components/ActionHistoryModal.tsx
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { X, Clipboard, Loader2, ArrowLeft } from '@/components/ui/lucide-memo';
import CollapsibleSection from './ui/CollapsibleSection';
import { toast } from 'sonner';
import { useUser } from '@clerk/nextjs';
import axios from 'axios';
import { useKernelStore } from '@/kernel/kernelStores';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';
import { useKernel } from '@/kernel/Kernel';
import type { TimelineEventCategory, UserAction } from '#types/types';

interface ActionHistoryModalProps {
  onClose?: () => void;
  projectId?: string;
}

const HISTORY_CATEGORIES: TimelineEventCategory[] = ['COMMUNICATION', 'AGENTIC', 'MUTATION'];
const HISTORY_LIMIT = 500;

const isNewDay = (currentTimestamp?: string, previousTimestamp?: string): boolean => {
  if (!previousTimestamp || !currentTimestamp) return true;
  const currentDate = new Date(currentTimestamp).toDateString();
  const previousDate = new Date(previousTimestamp).toDateString();
  return currentDate !== previousDate;
};

interface ActionHistoryRowProps {
  action: UserAction;
  previousTimestamp?: string;
  isOpen: boolean;
  onVisibilityChange: (actionId: string, isVisible: boolean) => void;
}

// Mémoïsé et propriétaire de son propre handler : la liste monte à HISTORY_LIMIT lignes,
// et un seul dépliage re-rendait les 500 CollapsibleSection sans cette frontière.
const ActionHistoryRow: React.FC<ActionHistoryRowProps> = React.memo(({
  action,
  previousTimestamp,
  isOpen,
  onVisibilityChange,
}) => {
  const showDateSeparator = isNewDay(action.timestamp, previousTimestamp);
  const dateSeparator = new Date(action.timestamp).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const hasPayload = Object.keys(action.payload).length > 0;
  const timestamp = new Date(action.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const handleVisibilityChange = useCallback((isVisible: boolean) => {
    onVisibilityChange(action.id, isVisible);
  }, [onVisibilityChange, action.id]);

  const title = useMemo(() => (
    <div className="grid grid-cols-[9ch_1fr] items-center gap-x-4">
      <span className="text-sm text-neutral-400 font-mono">{timestamp}</span>
      <span className="font-semibold text-neutral-100">{action.name}</span>
    </div>
  ), [timestamp, action.name]);

  return (
    <>
      {showDateSeparator && (
        <div className="pt-4 pb-2">
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-neutral-600"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-neutral-800 px-3 text-sm font-medium text-neutral-400">
                {dateSeparator}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="bg-neutral-700/50 rounded-lg border border-neutral-700/80 transition-shadow hover:shadow-lg hover:border-neutral-600">
        {hasPayload ? (
          <CollapsibleSection
            title={title}
            rightContent={null}
            isVisible={isOpen}
            onVisibilityChange={handleVisibilityChange}
          >
            <div className="ml-6">
               <div className="text-sm bg-neutral-900 p-3 rounded-md overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words font-mono text-neutral-300">
                  {JSON.stringify(action.payload, null, 2)}
                </pre>
              </div>
            </div>
          </CollapsibleSection>
        ) : (
          <div className="flex items-center p-3 cursor-default">
            <div className="w-5 mr-2"></div> {/* Spacer for chevron */}
            {title}
          </div>
        )}
      </div>
    </>
  );
});
ActionHistoryRow.displayName = 'ActionHistoryRow';

const ActionHistoryModal: React.FC<ActionHistoryModalProps> = ({ onClose, projectId }) => {
  const { kernel } = useKernel();
  const { user } = useUser();
  const storeProjectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const liveActionHistory = useKernelStore(state => state.actionHistory);
  const [dbHistory, setDbHistory] = useState<UserAction[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [activeUserEmail, setActiveUserEmail] = useState<string | null>(null);
  const resolvedProjectId = projectId ?? storeProjectId;

  const mergedHistory = useMemo(() => {
    const deduped = new Map<string, UserAction>();
    dbHistory.forEach((action) => deduped.set(action.id, action));
    liveActionHistory.forEach((action) => deduped.set(action.id, action));
    return Array.from(deduped.values()).sort(
      (left, right) =>
        new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime()
    );
  }, [dbHistory, liveActionHistory]);

  const historyAsc = useMemo(() => {
    return [...mergedHistory].sort(
      (left, right) =>
        new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
    );
  }, [mergedHistory]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    kernel.current?.closeModal();
  }, [onClose, kernel]);

  useEffect(() => {
    const fetchDbHistory = async () => {
      if (!resolvedProjectId || !kernel.current) {
        setDbHistory([]);
        return;
      }

      setIsHistoryLoading(true);
      try {
        const { ids, error } = await kernel.current.queryInventory('TimelineEvent', {
          filters: {
            targetEntityType: 'Project',
            targetEntityId: resolvedProjectId,
            category: HISTORY_CATEGORIES,
            sortDir: 'desc',
          },
          limit: HISTORY_LIMIT,
          force: true,
        });
        if (error) throw new Error(error);

        const registry = kernel.current?.getInventory().TimelineEvent.registry ?? {};
        setDbHistory(
          ids.flatMap((id) => {
            const event = registry[id];
            if (!event) return [];
            return [{
              id: event.id,
              name: event.eventType,
              payload: event.payload,
              timestamp: event.createdAt,
            }];
          })
        );
      } catch (error) {
        console.error('[ActionHistoryModal] Failed to fetch DB action history.', error);
        toast.error('Failed to load action history.');
      } finally {
        setIsHistoryLoading(false);
      }
    };

    void fetchDbHistory();
  }, [resolvedProjectId, kernel]);

  useEffect(() => {
    const fetchActiveUser = async () => {
      try {
        const response = await axios.post('/api/admin', { action: 'getImpersonationStatus' });
        const { isImpersonating, impersonatedUser } = response.data;
        if (isImpersonating && impersonatedUser?.email) {
          setActiveUserEmail(impersonatedUser.email);
        } else {
          // Not impersonating, use the logged-in user's email
          setActiveUserEmail(user?.primaryEmailAddress?.emailAddress || 'N/A');
        }
      } catch (error) {
        console.error("Failed to get impersonation status, falling back to current user.", error);
        // Fallback on error
        setActiveUserEmail(user?.primaryEmailAddress?.emailAddress || 'N/A');
      }
    };

    if (user) {
      fetchActiveUser();
    }
  }, [user]);

  const closeIcon = useMemo(() => <X size={24} />, []);
  const clipboardIcon = useMemo(() => <Clipboard size={16} className="mr-2" />, []);
  const backIcon = useMemo(() => <ArrowLeft size={16} className="mr-2" />, []);
  const loadingIcon = useMemo(() => <Loader2 size={16} className="mr-2 animate-spin" />, []);

  const handleGenerateReport = useCallback(async () => {
    setIsLoading(true);
    setReport(null);

    try {
      const reportData = await kernel.current?.buildProjectAssetReport(
        resolvedProjectId ?? '',
        historyAsc,
        activeUserEmail || 'N/A'
      );
      if (!reportData) {
        throw new Error('Cloud Asset Report generation failed.');
      }

      setReport(JSON.stringify(reportData, null, 2));
      toast.success("Cloud Asset Report generated successfully.");
    } catch (error) {
      console.error("Failed to generate report:", error);
      toast.error("Failed to generate report. See console for details.");
    } finally {
      setIsLoading(false);
    }
  }, [resolvedProjectId, historyAsc, activeUserEmail, kernel]);

  const handleCopyToClipboard = useCallback(() => {
    if (!report) return;
    navigator.clipboard.writeText(report).then(() => {
      toast.success("Report copied to clipboard!");
    }).catch(err => {
      toast.error("Failed to copy report.");
      console.error("Clipboard copy error:", err);
    });
  }, [report]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const handleVisibilityChange = useCallback((actionId: string, isVisible: boolean) => {
    setOpenSections(prev => ({
      ...prev,
      [actionId]: isVisible,
    }));
  }, []);

  const handleBackToHistory = useCallback(() => setReport(null), []);

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={handleClose}>
      <div
        className="bg-neutral-800 text-white rounded-lg shadow-xl w-11/12 max-w-6xl h-5/6 flex flex-col border border-neutral-700"
        onClick={stopPropagation}
      >
        <div className="flex justify-between items-center p-4 border-b border-neutral-700 flex-shrink-0">
          <h2 className="text-xl font-semibold">
            {report ? 'Cloud Asset Report' : 'Project Action History'}
          </h2>
          <div className="flex items-center gap-4">
            {report ? (
              <>
                <button
                  onClick={handleBackToHistory}
                  className="flex items-center bg-neutral-700 hover:bg-neutral-600 text-white text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
                >
                  {backIcon} Back to History
                </button>
                <button
                  onClick={handleCopyToClipboard}
                  className="flex items-center bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
                >
                  {clipboardIcon} Copy Report
                </button>
              </>
            ) : (
              <button
                onClick={handleGenerateReport}
                disabled={isLoading}
                className="flex items-center bg-green-600 hover:bg-green-500 disabled:bg-neutral-600 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
              >
                {isLoading ? loadingIcon : null}
                {isLoading ? 'Generating...' : 'Generate Asset Report'}
              </button>
            )}
            <button
              onClick={handleClose}
              className="text-neutral-400 hover:text-white rounded-full p-1 transition-colors"
              aria-label="Close action history"
            >
              {closeIcon}
            </button>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto custom-scrollbar p-6 space-y-2">
          {report ? (
            <div className="bg-neutral-900 p-4 rounded-lg border border-neutral-700">
              <pre className="text-xs text-neutral-300 whitespace-pre-wrap break-all font-mono">
                <code>{report}</code>
              </pre>
            </div>
          ) : isHistoryLoading ? (
            <div className="flex items-center justify-center h-full text-neutral-400 gap-3">
              <Loader2 size={18} className="animate-spin" />
              <p>Loading action history...</p>
            </div>
          ) : mergedHistory.length > 0 ? (
            mergedHistory.map((action, index) => (
              <ActionHistoryRow
                key={action.id}
                action={action}
                previousTimestamp={mergedHistory[index - 1]?.timestamp}
                isOpen={openSections[action.id] || false}
                onVisibilityChange={handleVisibilityChange}
              />
            ))
          ) : (
            <div className="flex items-center justify-center h-full text-neutral-500">
              <p>No actions have been recorded for this project yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ActionHistoryModal);

